import React, { useState, useEffect } from 'react';
import QRCode from 'qrcode';

interface AddressData {
    network: string;
    currency: string;
    wallet_address: string;
    limits: {
        min: number;
        max: number;
    };
}

interface AddressDisplayProps {
    addressData: AddressData;
    onGenerateNew: () => void;
    onUpdateAddress: (newAddress: string) => void;
    loading: boolean;
}

const AddressDisplay: React.FC<AddressDisplayProps> = ({
    addressData,
    onGenerateNew,
    onUpdateAddress,
    loading,
}) => {
    const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');
    const [showUpdateForm, setShowUpdateForm] = useState<boolean>(false);
    const [newAddress, setNewAddress] = useState<string>('');
    const [copySuccess, setCopySuccess] = useState<boolean>(false);
    const [showQRModal, setShowQRModal] = useState<boolean>(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);

    useEffect(() => {
        generateQRCode();
    }, [addressData.wallet_address]);

    const generateQRCode = async () => {
        try {
            const dataUrl = await QRCode.toDataURL(addressData.wallet_address, {
                width: 200,
                margin: 2,
                color: {
                    dark: '#000', // blue-800
                    light: '#ffffff',
                },
            });
            setQrCodeDataUrl(dataUrl);
        } catch (error) {
            // Failed to generate QR code
        }
    };

    const copyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(addressData.wallet_address);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        } catch (error) {
            // Failed to copy to clipboard
        }
    };

    const handleUpdateSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (newAddress.trim()) {
            onUpdateAddress(newAddress.trim());
            setShowUpdateForm(false);
            setNewAddress('');
        }
    };

    const handleGenerateNewClick = () => {
        setShowConfirmDialog(true);
    };

    const handleConfirmGenerate = () => {
        setShowConfirmDialog(false);
        onGenerateNew();
    };

    const handleCancelGenerate = () => {
        setShowConfirmDialog(false);
    };

    return (
        <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 mb-6 border border-gray-100">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                        <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    Wallet Address
                </h2>
                {/* QR Code - Mobile First */}
                {qrCodeDataUrl && (
                    <div className="block sm:hidden">
                        <button
                            onClick={() => setShowQRModal(true)}
                            className="bg-white rounded-lg p-2 shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200"
                        >
                            <img
                                src={qrCodeDataUrl}
                                alt="QR Code"
                                className="w-16 h-16 rounded"
                            />
                        </button>
                    </div>
                )}
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left Column - Network & Currency Info */}
                <div className="lg:col-span-2 space-y-4">
                    {/* Network & Currency Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-blue-700">Network</span>
                                <div className="w-6 h-6 bg-blue-200 rounded-md flex items-center justify-center">
                                    <svg className="w-3 h-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                    </svg>
                                </div>
                            </div>
                            <span className="text-lg font-bold text-blue-900">{addressData.network}</span>
                        </div>

                        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border border-green-200">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-green-700">Currency</span>
                                <div className="w-6 h-6 bg-green-200 rounded-md flex items-center justify-center">
                                    <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                                    </svg>
                                </div>
                            </div>
                            <span className="text-lg font-bold text-green-900">{addressData.currency}</span>
                        </div>
                    </div>

                    {/* Deposit Limits */}
                    <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4">
                        <h3 className="text-sm font-semibold text-amber-800 mb-3 flex items-center">
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                            Deposit Limits
                        </h3>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <div className="flex items-center justify-between">
                                <span className="text-amber-700 font-medium">Minimum:</span>
                                <span className="font-bold text-amber-900">${addressData.limits.min}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-amber-700 font-medium">Maximum:</span>
                                <span className="font-bold text-amber-900">${addressData.limits.max}</span>
                            </div>
                        </div>
                        <div className="mt-3 pt-3 border-t border-amber-300">
                            <p className="text-xs text-amber-700">
                                <span className="font-medium">Note:</span> Deposit fees will be calculated based on the amount you send and fees are exactly the same as dynamic wallet address.
                            </p>
                        </div>
                    </div>

                    {/* Payment Warning */}
                    <div className="bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-xl p-4">
                        <h3 className="text-sm font-semibold text-red-800 mb-3 flex items-center">
                            ⚠️ Important Payment Warning
                        </h3>
                        <div className="text-sm text-red-700 space-y-2">
                            <p className="font-medium">Before making any deposit, ensure you:</p>
                            <ul className="list-disc list-inside space-y-1 ml-2">
                                <li>Select the <strong>correct network</strong>: <span className="font-bold text-red-800">{addressData.network}</span></li>
                                <li>Select the <strong>correct currency</strong>: <span className="font-bold text-red-800">{addressData.currency}</span></li>
                            </ul>
                            <p className="font-semibold text-red-800 mt-2">
                                ⚠️ Any deposit to the wrong network or currency will be permanently lost and cannot be recovered!
                            </p>
                        </div>
                    </div>

                    {/* Wallet Address */}
                    <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                            Wallet Address
                        </h3>
                        <div className="flex flex-col sm:flex-row gap-3">
                            <input
                                type="text"
                                value={addressData.wallet_address}
                                readOnly
                                className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded-lg text-xs sm:text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                            />
                            <button
                                onClick={copyToClipboard}
                                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 whitespace-nowrap ${copySuccess
                                    ? 'bg-green-500 text-white'
                                    : 'bg-[#1d4df0] text-white hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-200'
                                    }`}
                            >
                                {copySuccess ? (
                                    <div className="flex items-center">
                                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        Copied!
                                    </div>
                                ) : (
                                    <div className="flex items-center">
                                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                        </svg>
                                        Copy
                                    </div>
                                )}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Right Column - QR Code (Desktop) */}
                {qrCodeDataUrl && (
                    <div className="hidden sm:block">
                        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 text-center sticky top-4">
                            <img
                                src={qrCodeDataUrl}
                                alt="QR Code"
                                className="w-full max-w-48 mx-auto rounded-lg"
                            />
                            <p className="text-sm text-gray-600 mt-3 font-medium">Scan to copy</p>
                            <p className="text-xs text-gray-500 mt-1">Mobile wallet compatible</p>
                        </div>
                    </div>
                )}
            </div>

            {/* Action Buttons */}
            <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex flex-col sm:flex-row gap-3">
                    <button
                        onClick={handleGenerateNewClick}
                        disabled={loading}
                        className="flex-1 bg-[#1d4df0] text-white px-6 py-3 rounded-xl hover:bg-blue-600 focus:outline-none focus:ring-4 focus:ring-blue-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-lg transition-all duration-200 transform hover:scale-105 disabled:transform-none"
                    >
                        <div className="flex items-center justify-center">
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            Generate New Address
                        </div>
                    </button>
                </div>
            </div>

            {/* Update Address Form */}
            {showUpdateForm && (
                <div className="mt-6 p-6 bg-gray-50 rounded-xl border border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Update Wallet Address</h3>
                    <form onSubmit={handleUpdateSubmit} className="space-y-4">
                        <div>
                            <label htmlFor="newAddress" className="block text-sm font-semibold text-gray-700 mb-2">
                                New Wallet Address
                            </label>
                            <input
                                type="text"
                                id="newAddress"
                                value={newAddress}
                                onChange={(e) => setNewAddress(e.target.value)}
                                placeholder="Enter new wallet address"
                                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all duration-200"
                                required
                            />
                        </div>
                        <div className="flex flex-col sm:flex-row gap-3">
                            <button
                                type="submit"
                                disabled={loading || !newAddress.trim()}
                                className="flex-1 bg-blue-500 text-white px-6 py-3 rounded-xl hover:bg-blue-600 focus:outline-none focus:ring-4 focus:ring-blue-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-lg transition-all duration-200"
                            >
                                Update Address
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setShowUpdateForm(false);
                                    setNewAddress('');
                                }}
                                className="flex-1 bg-gray-500 text-white px-6 py-3 rounded-xl hover:bg-gray-600 focus:outline-none focus:ring-4 focus:ring-gray-200 font-medium shadow-lg transition-all duration-200"
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* QR Code Modal */}
            {showQRModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl p-6 max-w-sm w-full">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900">QR Code</h3>
                            <button
                                onClick={() => setShowQRModal(false)}
                                className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="text-center">
                            <img
                                src={qrCodeDataUrl}
                                alt="QR Code"
                                className="w-64 h-64 mx-auto rounded-lg border border-gray-200"
                            />
                            <p className="text-sm text-gray-600 mt-4 font-medium">Scan to copy address</p>
                            <p className="text-xs text-gray-500 mt-1">Mobile wallet compatible</p>
                        </div>
                        <div className="mt-6 flex gap-3">
                            <button
                                onClick={copyToClipboard}
                                className="flex-1 bg-[#1d4df0] text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors duration-200 font-medium"
                            >
                                Copy Address
                            </button>
                            <button
                                onClick={() => setShowQRModal(false)}
                                className="flex-1 bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors duration-200 font-medium"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirmation Dialog */}
            {showConfirmDialog && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl p-6 max-w-md w-full">
                        <div className="flex items-center mb-4">
                            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4">
                                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                </svg>
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900">Generate New Address?</h3>
                        </div>
                        <div className="mb-6">
                            <p className="text-gray-700 mb-3">
                                Are you sure you want to generate a new wallet address?
                            </p>
                            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                <p className="text-sm text-red-800 font-medium">
                                    ⚠️ Warning: Any deposits made to your current address will be lost and cannot be recovered!
                                </p>
                            </div>
                        </div>
                        <div className="flex gap-3">
                            <button
                                onClick={handleCancelGenerate}
                                className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors duration-200 font-medium text-sm"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirmGenerate}
                                className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors duration-200 font-medium text-sm"
                            >
                                Yes
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AddressDisplay;
